import {
  DataStatus,
  Form,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';

import {useSelector} from 'react-redux';

import {isNil} from 'ramda';

import {selectBranchList, useGetRoundTypeQuery} from '@dms/api/core';
import {
  useCreateRoundingDefinitionMutation,
  useGetPaymentTypeEnumQuery,
  useGetRoundStepQuery,
  useUpdateRoundingDefinitionMutation,
} from '@dms/api/finance';
import {CreateRoundingDefinitionRequestBody, RoundingDefinitionResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, composePath, useNavigate} from 'shared/web';

import {
  documentRoundingDefinitionFormValidations,
  DocumentRoundingDefinitionFormValues,
  DocumentRoundingDefinitionDefaultValue,
} from '../utils/documentRoundingDefinitionFormValidations';
import {getEmptyValidityItem} from '../utils/getEmptyValidityItem';
import {ValidityList} from './ValidityList';

interface DocumentRoundingDefinitionsAddProps {
  roundingDefinition?: RoundingDefinitionResponseBody;
}

export const DocumentRoundingDefinitionForm = (props: DocumentRoundingDefinitionsAddProps) => {
  const isCreating = isNil(props.roundingDefinition);
  const navigate = useNavigate();

  const {data: {branchListItems: branchList} = {}} = useSelector(selectBranchList);
  const {
    data: paymentTypes,
    isError: isPaymentTypesError,
    isLoading: isPaymentTypesLoading,
  } = useGetPaymentTypeEnumQuery();
  const {
    data: roundStepList,
    isError: isRoundingStepError,
    isLoading: isRoundingStepLoading,
  } = useGetRoundStepQuery();
  const {
    data: roundTypeList,
    isError: isRoundTypeError,
    isLoading: isRoundTypeLoading,
  } = useGetRoundTypeQuery();

  const [createRoundingDefinition] = useCreateRoundingDefinitionMutation();
  const [updateRoundingDefinition] = useUpdateRoundingDefinitionMutation();

  const handleSubmit: FormSubmitHandler<DocumentRoundingDefinitionFormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const targetRoute = composePath(settingsRoutes.documentRoundingDefinitions);

    if (isCreating) {
      await createRoundingDefinition({
        createRoundingDefinitionRequestBody: data as CreateRoundingDefinitionRequestBody,
      })
        .unwrap()
        .then(() => {
          showNotification.success(
            i18n.t('entity.accounting.notifications.roundingDefinitionCreated')
          );
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(targetRoute);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    } else if (props.roundingDefinition) {
      await updateRoundingDefinition({
        id: props.roundingDefinition.id,
        updateRoundingDefinitionRequestBody: data,
      })
        .unwrap()
        .then(() => {
          showNotification.success(
            i18n.t('entity.accounting.notifications.roundingDefinitionUpdated')
          );
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(targetRoute);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }
  };

  return (
    <DataStatus
      isLoading={isPaymentTypesLoading || isRoundingStepLoading || isRoundTypeLoading}
      isError={isPaymentTypesError || isRoundingStepError || isRoundTypeError}
    >
      <Form<DocumentRoundingDefinitionFormValues>
        experimentalZodSchema={documentRoundingDefinitionFormValidations(
          !isCreating,
          props.roundingDefinition
        )}
        defaultValues={{
          validities: [getEmptyValidityItem()],
          ...(props.roundingDefinition as DocumentRoundingDefinitionDefaultValue),
        }}
        onSubmit={handleSubmit}
        shouldWatchForUnsavedChanges
      >
        {(control, formApi, navigateWithoutBlocker) => (
          <SettingsSection>
            <VStack spacing={4}>
              <Grid columns={2}>
                <FormField
                  control={control}
                  type="choice"
                  name="branchId"
                  options={
                    branchList?.map((item) => ({
                      value: item.id,
                      label: item.marketingName ?? '',
                    })) ?? []
                  }
                  label={i18n.t('entity.branch.labels.branch')}
                  data-testid={testIds.settings.documentRoundingDefinitionsEdit('branchId')}
                  isRequired
                  isDisabled={!isCreating}
                />
                <FormField
                  control={control}
                  type="choice"
                  name="paymentType"
                  options={
                    paymentTypes?.map((item) => ({value: item.key, label: item.value})) ?? []
                  }
                  label={i18n.t('entity.bank.labels.paymentType')}
                  data-testid={testIds.settings.documentRoundingDefinitionsEdit('paymentType')}
                  isRequired
                  isDisabled={!isCreating}
                />
              </Grid>
              <ValidityList
                roundStepList={roundStepList}
                roundTypeList={roundTypeList}
                control={control}
                formApi={formApi}
              />
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    variant: 'secondary',
                    onClick: () =>
                      navigateWithoutBlocker(
                        composePath(settingsRoutes.documentRoundingDefinitions)
                      ),
                    title: i18n.t('general.actions.cancel'),
                    'data-testid': testIds.settings.documentRoundingDefinitionsEdit('cancel'),
                  },
                  {
                    type: 'form-button',
                    buttonType: 'submit',
                    control,
                    title: isCreating
                      ? i18n.t('general.actions.create')
                      : i18n.t('general.actions.save'),
                    'data-testid': testIds.settings.documentRoundingDefinitionsEdit('submit'),
                  },
                ]}
              />
            </VStack>
          </SettingsSection>
        )}
      </Form>
    </DataStatus>
  );
};
